import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  MailIcon,
  KeyRoundIcon,
  ArrowRightIcon,
  Loader2Icon,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  authControllerMagicLink,
  authControllerPassword,
} from "@/client/sdk.gen";
import { ApiError } from "@/lib/api-client";
import { ThemeToggle } from "@/components/theme-toggle";
import { useSessionContext } from "@/lib/session";

export const Route = createFileRoute("/login/")({
  component: LoginPage,
});

/**
 * Sign-in without SSO.
 *
 * The magic link is the regular route. The password fallback is deliberately
 * not an equal second option but the fallback for when a Sparkasse's mail
 * filter swallows the link.
 */
function LoginPage() {
  const navigate = useNavigate();
  const { user, refresh } = useSessionContext();
  const [mode, setMode] = React.useState<"link" | "password">("link");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (user) void navigate({ to: "/" });
  }, [user, navigate]);

  async function requestLink(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await authControllerMagicLink({ body: { email } });
      setSent(true);
    } catch {
      // Reveal nothing about whether the address exists, even on failure.
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  async function loginWithPassword(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await authControllerPassword({ body: { email, password } });
      await refresh();
      void navigate({ to: "/" });
    } catch (error) {
      toast.error(
        error instanceof ApiError ? error.message : "Anmeldung fehlgeschlagen.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="relative flex min-h-dvh items-center justify-center px-6 py-16">
      <div className="absolute top-4 right-4">
        <ThemeToggle side="bottom" />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-md bg-white ring-1 ring-[var(--rule)]">
            <img
              src="/neueleben_logo.jpeg"
              alt="neue leben"
              width={112}
              height={112}
              className="h-11 w-11 object-contain"
            />
          </span>
          <p className="eyebrow mt-4">neue leben</p>
          <h1 className="font-display mt-2 text-3xl font-semibold tracking-tight">
            KI-Coach
          </h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {mode === "link" ? "Anmeldelink anfordern" : "Mit Passwort anmelden"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sent && mode === "link" ? (
              <div className="space-y-4">
                <p className="text-sm">
                  Wenn für <span className="font-medium">{email}</span> ein
                  Zugang besteht, ist der Anmeldelink unterwegs. Er ist 30
                  Minuten gültig.
                </p>
                <p className="text-muted-foreground text-sm">
                  Keine Mail bekommen? Der Mailfilter deiner Sparkasse kann den
                  Link zurückhalten. Wende dich in dem Fall an deine
                  Ansprechperson, sie kann ein Einmal-Passwort vergeben.
                </p>
                <Button variant="outline" onClick={() => setSent(false)}>
                  Andere Adresse verwenden
                </Button>
              </div>
            ) : (
              <form
                onSubmit={mode === "link" ? requestLink : loginWithPassword}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <Label htmlFor="email">Dienstliche E-Mail-Adresse</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="vorname.nachname@sparkasse.de"
                  />
                </div>

                {mode === "password" && (
                  <div className="space-y-2">
                    <Label htmlFor="password">Passwort</Label>
                    <Input
                      id="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={busy}>
                  {busy ? (
                    <Loader2Icon className="size-4 animate-spin" />
                  ) : mode === "link" ? (
                    <MailIcon className="size-4" />
                  ) : (
                    <ArrowRightIcon className="size-4" />
                  )}
                  {mode === "link" ? "Anmeldelink senden" : "Anmelden"}
                </Button>
              </form>
            )}

            <div className="mt-6 border-t pt-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="-ml-2.5 text-muted-foreground"
                onClick={() => {
                  setMode(mode === "link" ? "password" : "link");
                  setSent(false);
                }}
              >
                <KeyRoundIcon />
                {mode === "link"
                  ? "Stattdessen Passwort verwenden"
                  : "Zurück zum Anmeldelink"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
